// Phase 8 web client root.
//
// Provider order (outermost → innermost):
//   <IntlProvider>      ← Plan 07 wraps here (i18n)
//   <ThemeProvider>     ← Plan 08-03 owns this (CSS vars + Pixi uniforms)
//   <MotionProvider>    ← Plan 08-08 wraps here (reduced-motion gate)
//   <AuthProvider>      ← Plan 08-02 owns this (auth/$bearer)
//   <Router>            ← Plan 08-02 owns this (route tree)
//
// Insertion sites for downstream plans:
//   <PROVIDER:INTL>     ← Plan 07 wraps here
//   <PROVIDER:MOTION>   ← Plan 08-08 wraps here
//
// Apple Sign-In end-to-end verified in production on 2026-06-02 with no
// CSP violations from inline scripts — the SDK (v1.5.6) only loads its
// hosted JS from https://appleid.cdn-apple.com, which is already in the
// `script-src` allowlist. The placeholder hash + boot-time warning have
// been removed. If a future Apple SDK version reintroduces an inline
// bootstrap script, follow apps/web/scripts/capture-csp-hashes.md to
// capture the SHA-256 from the CSP violation report and add it back to
// the script-src directive in apps/web/public/_headers.

// Pixi v8 uses `new Function()` for shader compilation by default, which
// requires `unsafe-eval` in the page CSP. The `pixi.js/unsafe-eval` sub-path
// registers a precompiled shader system that avoids eval entirely, satisfying
// our strict CSP (apps/web/public/_headers). MUST be imported BEFORE any
// Pixi Application/Renderer is constructed; placing it at the top of the
// entry module guarantees the side effect runs before any downstream Pixi
// usage in the React tree.
import 'pixi.js/unsafe-eval'

import { type JSX, type ReactNode, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { AuthProvider } from './auth/AuthProvider.js'
import { RootApp } from './game/RootApp.js'
import { ThemeProvider } from './game/theme/ThemeProvider'
import { IntlContextProvider } from './i18n/IntlContextProvider.js'
import { useMotionAttributeBinding } from './stores/motion.js'
import './styles/globals.css'
import './styles/theme.css'
import './styles/pieces.css'

// F&F POC defense-in-depth — the SPA is also reachable at
// runegambit-web.pages.dev (Cloudflare Pages production alias), which is NOT
// behind the Cloudflare Access gate configured on app.runegambit.com.
// Redirect any non-canonical hostname to the gated origin BEFORE the React
// tree mounts. Bundled module (CSP `'self'`-served), so it isn't blocked the
// way an inline <script> in index.html would be. The backend CORS allowlist
// also rejects non-canonical origins (apps/backend/src/index.ts) — defense in
// depth if a JS edit bypasses this redirect.
if (
  typeof window !== 'undefined' &&
  location.hostname !== 'app.runegambit.com' &&
  location.hostname !== 'localhost' &&
  location.hostname !== '127.0.0.1'
) {
  location.replace(
    'https://app.runegambit.com' +
      location.pathname +
      location.search +
      location.hash,
  )
}

/**
 * MotionProvider — mounts useMotionAttributeBinding() once at the root so
 * `<html data-motion="reduced|full">` mirrors $reducedMotion across the
 * whole tree (single source of truth, no twin stylesheets, no per-component
 * if-checks). Per Plan 08-08 (DEC-004 OR-union; NY-08-02).
 */
function MotionProvider({ children }: { children: ReactNode }): JSX.Element {
  useMotionAttributeBinding()
  return <>{children}</>
}

const root = document.getElementById('app')
if (!root) {
  throw new Error('Runegambit boot: #app mount node missing from index.html')
}

createRoot(root).render(
  <StrictMode>
    <IntlContextProvider>
      <ThemeProvider>
        <MotionProvider>
          <AuthProvider>
            {/* Phase 18 Plan 03 — POLISH-08 RootApp wraps Router + Footer and
                mounts the $locale → documentElement.lang mirror plus the
                SkipLink as the first focusable element of the app. */}
            <RootApp />
          </AuthProvider>
        </MotionProvider>
      </ThemeProvider>
    </IntlContextProvider>
  </StrictMode>,
)
